/**
 * EF-PARSER-ALIAS-TABLE-V1: Parser Alias Admin Page
 * 
 * Admin-only page for managing parser aliases used in text normalization.
 * Supports multilingual aliases for commands, supplements, tasks, and crew roles.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Search, RefreshCw, Globe, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  getParserAliases, createParserAlias, updateParserAlias, 
  deleteParserAlias, seedDefaultParserAliases, resolveParserAlias 
} from '../lib/api';

const ALIAS_TYPES = [
  { value: 'command', label: 'Command', description: 'Parser commands (project, shift, etc.)' },
  { value: 'supplement', label: 'Supplement', description: 'Supplement names (Meal Allowance, Travel, etc.)' },
  { value: 'task', label: 'Task', description: 'Task definitions (Setup, Teardown, etc.)' },
  { value: 'crew_role', label: 'Crew Role', description: 'Crew role names (Crew Chief, Technician, etc.)' },
];

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'Français' },
  { value: 'it', label: 'Italiano' },
];

const EMPTY_ALIAS = {
  alias: '',
  normalized_value: '',
  alias_type: 'command',
  language: 'en',
  is_active: true,
  notes: '',
};

export default function ParserAliasPage() {
  const [aliases, setAliases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAlias, setEditingAlias] = useState(null);
  const [formData, setFormData] = useState(EMPTY_ALIAS);
  const [saving, setSaving] = useState(false);
  
  // Filters
  const [filterType, setFilterType] = useState('all');
  const [filterLanguage, setFilterLanguage] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Test dialog
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testInput, setTestInput] = useState('');
  const [testType, setTestType] = useState('command');
  const [testResult, setTestResult] = useState(null);

  const fetchAliases = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType && filterType !== 'all') params.alias_type = filterType;
      if (filterLanguage && filterLanguage !== 'all') params.language = filterLanguage;
      if (searchQuery) params.search = searchQuery;
      params.limit = 500;
      
      const response = await getParserAliases(params);
      setAliases(response.data || []);
    } catch (err) {
      toast.error('Failed to load aliases');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterLanguage, searchQuery]);

  useEffect(() => {
    fetchAliases();
  }, [fetchAliases]);

  const handleCreate = () => {
    setEditingAlias(null);
    setFormData(EMPTY_ALIAS);
    setDialogOpen(true);
  };

  const handleEdit = (alias) => {
    setEditingAlias(alias);
    setFormData({
      alias: alias.alias,
      normalized_value: alias.normalized_value,
      alias_type: alias.alias_type,
      language: alias.language,
      is_active: alias.is_active,
      notes: alias.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.alias || !formData.normalized_value) {
      toast.error('Alias and normalized value are required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingAlias) {
        await updateParserAlias(editingAlias.id, formData);
        toast.success('Alias updated');
      } else {
        await createParserAlias(formData);
        toast.success('Alias created');
      }
      setDialogOpen(false);
      fetchAliases();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to save alias';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (alias) => {
    if (!confirm(`Delete alias "${alias.alias}"?`)) return;
    
    try {
      await deleteParserAlias(alias.id);
      toast.success('Alias deleted');
      fetchAliases();
    } catch (err) {
      toast.error('Failed to delete alias');
    }
  };

  const handleSeedDefaults = async () => {
    if (!confirm('Seed default aliases? This will add missing aliases without overwriting existing ones.')) return;
    
    try {
      const response = await seedDefaultParserAliases();
      toast.success(`Seeded ${response.data.imported} new aliases (${response.data.skipped} skipped)`);
      fetchAliases();
    } catch (err) {
      toast.error('Failed to seed defaults');
    }
  };

  const handleTestResolve = async () => {
    if (!testInput) return;
    
    try {
      const response = await resolveParserAlias(testInput, testType);
      setTestResult(response.data);
    } catch (err) {
      toast.error('Resolution failed');
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'command': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'supplement': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'task': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'crew_role': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getLanguageFlag = (lang) => {
    switch (lang) {
      case 'en': return '🇬🇧';
      case 'fr': return '🇫🇷';
      case 'it': return '🇮🇹';
      default: return '🌐';
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="parser-alias-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Parser Aliases</h1>
          <p className="text-sm text-zinc-400 mt-1">
            Manage text normalization aliases for multilingual parsing
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setTestDialogOpen(true)}
            data-testid="test-resolve-btn"
          >
            <Globe className="h-4 w-4 mr-2" />
            Test Resolve
          </Button>
          <Button 
            variant="outline" 
            onClick={handleSeedDefaults}
            data-testid="seed-defaults-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Seed Defaults
          </Button>
          <Button onClick={handleCreate} data-testid="create-alias-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Alias
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center bg-zinc-900 p-4 rounded-lg border border-zinc-800">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input
              placeholder="Search aliases..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-zinc-800 border-zinc-700"
              data-testid="search-input"
            />
          </div>
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700" data-testid="filter-type">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {ALIAS_TYPES.map(t => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterLanguage} onValueChange={setFilterLanguage}>
          <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700" data-testid="filter-lang">
            <SelectValue placeholder="All Langs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Languages</SelectItem>
            {LANGUAGES.map(l => (
              <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-zinc-900 rounded-lg border border-zinc-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-400">Alias</TableHead>
              <TableHead className="text-zinc-400">Normalized Value</TableHead>
              <TableHead className="text-zinc-400">Type</TableHead>
              <TableHead className="text-zinc-400">Language</TableHead>
              <TableHead className="text-zinc-400">Status</TableHead>
              <TableHead className="text-zinc-400 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-zinc-500">
                  Loading...
                </TableCell>
              </TableRow>
            ) : aliases.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-zinc-500">
                  No aliases found. Click "Seed Defaults" to add initial aliases.
                </TableCell>
              </TableRow>
            ) : (
              aliases.map((alias) => (
                <TableRow 
                  key={alias.id} 
                  className="border-zinc-800"
                  data-testid={`alias-row-${alias.id}`}
                >
                  <TableCell className="font-medium text-white">{alias.alias}</TableCell>
                  <TableCell className="text-zinc-300">{alias.normalized_value}</TableCell>
                  <TableCell>
                    <Badge className={`${getTypeColor(alias.alias_type)} border`}>
                      {alias.alias_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-lg mr-1">{getLanguageFlag(alias.language)}</span>
                    <span className="text-zinc-400 text-sm uppercase">{alias.language}</span>
                  </TableCell>
                  <TableCell>
                    {alias.is_active ? (
                      <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                        <Check className="h-3 w-3 mr-1" /> Active
                      </Badge>
                    ) : (
                      <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">
                        <X className="h-3 w-3 mr-1" /> Inactive
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleEdit(alias)}
                      data-testid={`edit-alias-${alias.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-400 hover:text-red-300"
                      onClick={() => handleDelete(alias)}
                      data-testid={`delete-alias-${alias.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingAlias ? 'Edit Alias' : 'Create Alias'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Alias (input)</label>
                <Input
                  value={formData.alias}
                  onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
                  placeholder="e.g., repas, péage, montage"
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="alias-input"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Normalized Value (output)</label>
                <Input
                  value={formData.normalized_value}
                  onChange={(e) => setFormData({ ...formData, normalized_value: e.target.value })}
                  placeholder="e.g., Meal Allowance, Travel Toll"
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="normalized-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Type</label>
                <Select 
                  value={formData.alias_type} 
                  onValueChange={(v) => setFormData({ ...formData, alias_type: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ALIAS_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Language</label>
                <Select 
                  value={formData.language} 
                  onValueChange={(v) => setFormData({ ...formData, language: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="lang-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map(l => (
                      <SelectItem key={l.value} value={l.value}>
                        {getLanguageFlag(l.value)} {l.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Notes (optional)</label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Admin notes..."
                className="bg-zinc-800 border-zinc-700"
                data-testid="notes-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded border-zinc-600 bg-zinc-800"
                data-testid="active-checkbox"
              />
              <label htmlFor="is_active" className="text-sm text-zinc-300">Active</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-alias-btn">
              {saving ? 'Saving...' : (editingAlias ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Resolve Dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Test Alias Resolution</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-2">
                <label className="text-sm text-zinc-400">Input Text</label>
                <Input
                  value={testInput}
                  onChange={(e) => setTestInput(e.target.value)}
                  placeholder="e.g., repas, projet, machiniste"
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="test-input"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Type</label>
                <Select value={testType} onValueChange={setTestType}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ALIAS_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={handleTestResolve} className="w-full" data-testid="resolve-btn">
              Resolve
            </Button>
            {testResult && (
              <div className="p-4 rounded-lg bg-zinc-800 border border-zinc-700">
                <div className="text-sm text-zinc-400 mb-2">Result:</div>
                <div className="flex items-center gap-4">
                  <span className="text-white font-medium">{testResult.input}</span>
                  <span className="text-zinc-500">→</span>
                  {testResult.found ? (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                      {testResult.resolved}
                    </Badge>
                  ) : (
                    <Badge className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                      Not found (no match)
                    </Badge>
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
